import { FundingNoticeContent, type FundingNoticeData } from '../components/FundingNoticeContent';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../constants/notifications';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../services/api';
import { notificationActionPath } from '../utils/notificationLinks';
import { localizeNotification } from '../utils/notificationText';

interface NotificationRecord {
  id: string;
  definitionId: string;
  eventKey: string;
  type: string;
  title: string;
  body: string;
  recipientUserId: string;
  recipientType: string;
  channels: string[];
  priority: string;
  read: boolean;
  readAt: string | null;
  metadata: Record<string, unknown> & {
    icon?: string;
    color?: string;
    actionUrl?: string;
  };
  createdAt: string;
}

export function Notifications() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotif, setSelectedNotif] = useState<NotificationRecord | null>(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterRead, setFilterRead] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const loadNotifications = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      const data = await notificationApi.getMyNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications(true);
    const interval = setInterval(loadNotifications, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n));
    } catch {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      const now = new Date().toISOString();
      setNotifications(prev => prev.map(n => ({ ...n, read: true, readAt: n.readAt || now })));
    } catch {}
  };

  const types = useMemo(() => [...new Set(notifications.map(n => n.type))].sort(), [notifications]);
  const priorities = ['high', 'normal', 'low'];
  const unreadCount = notifications.filter(n => !n.read).length;
  const selectedActionPath = selectedNotif
    ? notificationActionPath(selectedNotif, user)
    : null;

  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (filterType && n.type !== filterType) return false;
      if (filterPriority && n.priority !== filterPriority) return false;
      if (filterRead === 'true' && !n.read) return false;
      if (filterRead === 'false' && n.read) return false;
      if (filterSearch && !n.title.toLowerCase().includes(filterSearch.toLowerCase()) && !n.body.toLowerCase().includes(filterSearch.toLowerCase())) return false;
      return true;
    });
  }, [notifications, filterType, filterPriority, filterRead, filterSearch]);

  const clearFilters = () => {
    setFilterType(''); setFilterPriority(''); setFilterRead(''); setFilterSearch('');
  };

  const getPriorityBadge = (priority: string) => {
    const map: Record<string, string> = { high: 'danger', normal: 'primary', low: 'secondary' };
    return `bg-${map[priority] || 'secondary'}`;
  };

  const getPriorityLabel = (priority: string) => {
    const map: Record<string, string> = { high: t('notifications.priority.high'), normal: t('notifications.priority.normal'), low: t('notifications.priority.low') };
    return map[priority] || priority;
  };

  const getChannelBadge = (channel: string) => {
    const map: Record<string, { color: string; label: string }> = {
      in_app: { color: 'bg-info', label: 'In-app' },
      email: { color: 'bg-primary', label: 'E-post' },
      sms: { color: 'bg-warning text-dark', label: 'SMS' }
    };
    return map[channel] || { color: 'bg-secondary', label: channel };
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('notifications.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('notifications.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('notifications.description')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3 mb-md-0">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-primary">{notifications.length}</div>
              <small className="text-muted">{t('notifications.stats.total')}</small>
            </div>
          </Card>
        </div>
        <div className="col-md-4 mb-3 mb-md-0">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-danger">{unreadCount}</div>
              <small className="text-muted">{t('notifications.stats.unread')}</small>
            </div>
          </Card>
        </div>
        <div className="col-md-4">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-success">{notifications.length - unreadCount}</div>
              <small className="text-muted">{t('notifications.stats.read')}</small>
            </div>
          </Card>
        </div>
      </div>

      <Card>
        {/* Filters + mark all */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">{t('notifications.logTitle')}</h6>
          {unreadCount > 0 && (
            <button className="btn btn-sm btn-outline-primary" onClick={handleMarkAllAsRead}>
              <i className="feather-check-circle me-1"></i>
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-4">
            <input type="text" className="form-control form-control-sm" placeholder={t('notifications.filters.search')} value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
          </div>
          <div className="col-4 col-md-2">
            <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">{t('notifications.filters.allTypes')}</option>
              {types.map(ty => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </div>
          <div className="col-4 col-md-2">
            <select className="form-select form-select-sm" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">{t('notifications.filters.allPriorities')}</option>
              {priorities.map(p => <option key={p} value={p}>{getPriorityLabel(p)}</option>)}
            </select>
          </div>
          <div className="col-4 col-md-2">
            <select className="form-select form-select-sm" value={filterRead} onChange={e => setFilterRead(e.target.value)}>
              <option value="">{t('notifications.filters.allReadStatus')}</option>
              <option value="false">{t('notifications.filters.unread')}</option>
              <option value="true">{t('notifications.filters.read')}</option>
            </select>
          </div>
          <div className="col-auto">
            <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
              <i className="feather-x me-1"></i>{t('notifications.filters.clear')}
            </button>
          </div>
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="feather-inbox fs-1 d-block mb-3"></i>
            <p>{t('notifications.noNotifications')}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>{t('notifications.columns.date')}</th>
                      <th>{t('notifications.columns.type')}</th>
                      <th>{t('notifications.columns.title')}</th>
                      <th>{t('notifications.columns.priority')}</th>
                      <th>{t('notifications.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNotifications.map(n => (
                      <tr key={n.id} className={!n.read ? 'fw-semibold' : ''} style={{ cursor: 'pointer' }} onClick={() => { handleMarkAsRead(n.id); setSelectedNotif(n); }}>
                        <td><small>{new Date(n.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</small></td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            <i className={`feather-${n.metadata?.icon || 'bell'} me-1`}></i>
                            {n.type}
                          </span>
                        </td>
                        <td>
                          <div>{localizeNotification(n, t).title}</div>
                          <small className="text-muted text-truncate d-block" style={{ maxWidth: '400px' }}>{localizeNotification(n, t).body}</small>
                        </td>
                        <td><span className={`badge ${getPriorityBadge(n.priority)}`} style={{ fontSize: '0.7em' }}>{getPriorityLabel(n.priority)}</span></td>
                        <td>
                          {n.read ? (
                            <span className="text-muted small"><i className="feather-check me-1"></i>{t('notifications.readLabel')}</span>
                          ) : (
                            <span className="text-primary small"><i className="feather-circle me-1"></i>{t('notifications.unreadLabel')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="d-md-none">
              {filteredNotifications.map(n => (
                <div
                  key={n.id}
                  className={`border rounded p-3 mb-2 ${!n.read ? 'bg-light border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { handleMarkAsRead(n.id); setSelectedNotif(n); }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <span className={`badge bg-${n.metadata?.color || 'secondary'} rounded-circle p-1 flex-shrink-0 mt-1`}>
                      <i className={`feather-${n.metadata?.icon || 'bell'}`} style={{ fontSize: '12px' }}></i>
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className={`small ${!n.read ? 'fw-bold' : ''}`}>{localizeNotification(n, t).title}</div>
                      <div className="text-muted small text-truncate">{localizeNotification(n, t).body}</div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                        {new Date(n.createdAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {' · '}<span className={`badge ${getPriorityBadge(n.priority)}`} style={{ fontSize: '0.65em' }}>{getPriorityLabel(n.priority)}</span>
                      </div>
                    </div>
                    {!n.read && <span className="bg-primary rounded-circle flex-shrink-0" style={{ width: 8, height: 8, marginTop: 6 }}></span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedNotif && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setSelectedNotif(null)}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <span className={`badge bg-${selectedNotif.metadata?.color || 'secondary'} rounded-circle p-2`}>
                    <i className={`feather-${selectedNotif.metadata?.icon || 'bell'}`}></i>
                  </span>
                  {localizeNotification(selectedNotif, t).title}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedNotif(null)}></button>
              </div>
              <div className="modal-body">
                {selectedNotif.metadata?.fundingNotification === true && Array.isArray((selectedNotif.metadata.fundingSnapshot as FundingNoticeData | undefined)?.stages) ? <FundingNoticeContent eventKey={selectedNotif.eventKey} data={selectedNotif.metadata.fundingSnapshot as FundingNoticeData} /> : <div className="mb-3 p-3 bg-light rounded">
                  <p className="mb-0 text-explain text-break">{localizeNotification(selectedNotif, t).body}</p>
                </div>}
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted fw-semibold" style={{ width: '120px' }}>{t('notifications.columns.type')}</td>
                      <td><span className="badge bg-light text-dark border">{selectedNotif.type}</span></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('notifications.columns.priority')}</td>
                      <td><span className={`badge ${getPriorityBadge(selectedNotif.priority)}`}>{getPriorityLabel(selectedNotif.priority)}</span></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('notifications.columns.channels')}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {selectedNotif.channels.map(c => {
                            const ch = getChannelBadge(c);
                            return <span key={c} className={`badge ${ch.color}`}>{ch.label}</span>;
                          })}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('notifications.columns.date')}</td>
                      <td>{new Date(selectedNotif.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('notifications.columns.status')}</td>
                      <td>
                        {selectedNotif.read ? (
                          <span className="text-success"><i className="feather-check-circle me-1"></i>{t('notifications.readLabel')}{selectedNotif.readAt ? ` — ${new Date(selectedNotif.readAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                        ) : (
                          <span className="text-primary"><i className="feather-circle me-1"></i>{t('notifications.unreadLabel')}</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                {selectedActionPath && (
                  <button className={selectedNotif.metadata?.fundingNotification ? "btn btn-success" : "btn btn-primary"} onClick={() => { setSelectedNotif(null); navigate(selectedActionPath); }}>
                    <i className="feather-arrow-right me-1"></i>
                    {selectedNotif.metadata?.fundingNotification ? t(selectedNotif.eventKey === 'payment.funding_fulfilled' ? 'fundingNotice.projectAccount.openProject' : 'fundingNotice.projectAccount.action') : t('notifications.goTo', 'Gå till')}
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => setSelectedNotif(null)}>{t('common.close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
